// components/extras/ExtraDialogs.js
// Dialog per cambio categoria, rifiuto, upload PDF firmato, upload fornitore, modifica, sconto

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { 
  AlertTriangle, Save, Upload, XCircle, FileText 
} from 'lucide-react';

// Dialog cambio categoria con modifiche non salvate
export const ChangeCategoryDialog = ({
  open,
  onOpenChange,
  onSaveThenChange,
  onConfirmChange
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Modifiche non salvate
        </DialogTitle>
        <DialogDescription>
          Hai delle modifiche non salvate. Vuoi salvare prima di cambiare categoria?
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col gap-2 mt-4">
        <Button className="btn-primary w-full" onClick={onSaveThenChange}>
          <Save className="w-4 h-4 mr-2" />
          Salva e cambia
        </Button>
        <Button
          variant="outline"
          className="w-full text-red-600 hover:bg-red-50 border-red-200"
          onClick={onConfirmChange}
        >
          Cambia senza salvare
        </Button>
        <Button variant="ghost" className="w-full" onClick={() => onOpenChange(false)}>
          Annulla
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// Dialog rifiuto extra
export const RejectDialog = ({
  open,
  onOpenChange,
  rejectReason,
  setRejectReason,
  onReject,
  processingAction
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-red-600">
          <XCircle className="w-5 h-5" />
          Rifiuta Extra
        </DialogTitle>
        <DialogDescription>
          Inserisci il motivo del rifiuto. Il tecnico riceverà una notifica.
        </DialogDescription>
      </DialogHeader>
      <div className="mt-4">
        <Textarea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Motivo del rifiuto..."
          rows={4}
          data-testid="reject-reason-textarea"
        />
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Annulla
        </Button>
        <Button 
          variant="destructive" 
          onClick={onReject}
          disabled={!rejectReason.trim() || processingAction}
          data-testid="confirm-reject-btn"
        >
          Conferma Rifiuto
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Dialog upload PDF firmato
export const UploadSignedDialog = ({
  open,
  onOpenChange,
  selectedFile,
  onFileSelect,
  onUpload,
  uploadingFile
}) => (
  <Dialog open={open} onOpenChange={(openState) => {
    onOpenChange(openState);
  }}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-amber-600">
          <Upload className="w-5 h-5" />
          Carica PDF Firmato
        </DialogTitle>
        <DialogDescription>
          Carica il documento Extra firmato dal cliente (scansione PDF).
        </DialogDescription>
      </DialogHeader>
      <div className="mt-4 space-y-4">
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <input
            type="file"
            accept=".pdf"
            onChange={onFileSelect}
            className="hidden"
            id="signed-pdf-input"
            data-testid="signed-pdf-input"
          />
          <label htmlFor="signed-pdf-input" className="cursor-pointer">
            <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">
              {selectedFile ? selectedFile.name : 'Clicca per selezionare un file PDF'}
            </p>
            <p className="text-xs text-gray-400 mt-1">Max 10MB</p>
          </label>
        </div>
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Annulla
        </Button>
        <Button 
          onClick={onUpload}
          disabled={!selectedFile || uploadingFile}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="confirm-upload-signed-btn"
        >
          {uploadingFile ? 'Caricamento...' : 'Carica PDF'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Dialog upload documenti fornitore
export const UploadSupplierDialog = ({
  open,
  onOpenChange,
  selectedFile,
  onFileSelect,
  supplierDocType,
  setSupplierDocType,
  onUpload,
  uploadingFile
}) => (
  <Dialog open={open} onOpenChange={(openState) => {
    onOpenChange(openState);
  }}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-purple-600">
          <Upload className="w-5 h-5" />
          Carica Documento Fornitore
        </DialogTitle>
        <DialogDescription>
          Carica il preventivo o l'ordine dal fornitore.
        </DialogDescription>
      </DialogHeader>
      <div className="mt-4 space-y-4">
        <div>
          <Label>Tipo documento</Label>
          <Select value={supplierDocType} onValueChange={setSupplierDocType}>
            <SelectTrigger data-testid="supplier-doc-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="preventivo">Preventivo Fornitore</SelectItem>
              <SelectItem value="ordine">Ordine Fornitore</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <input
            type="file"
            accept=".pdf"
            onChange={onFileSelect}
            className="hidden"
            id="supplier-pdf-input"
            data-testid="supplier-pdf-input"
          />
          <label htmlFor="supplier-pdf-input" className="cursor-pointer">
            <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">
              {selectedFile ? selectedFile.name : 'Clicca per selezionare un file PDF'}
            </p>
            <p className="text-xs text-gray-400 mt-1">Max 10MB</p>
          </label>
        </div>
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Annulla
        </Button>
        <Button 
          onClick={onUpload}
          disabled={!selectedFile || uploadingFile}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="confirm-upload-supplier-btn"
        >
          {uploadingFile ? 'Caricamento...' : 'Carica Documento'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Dialog richiesta modifica (Tecnico)
export const ModificationDialog = ({
  open,
  onOpenChange,
  modificationNotes,
  setModificationNotes,
  onRequestModification,
  processingAction
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-amber-600">
          <AlertTriangle className="w-5 h-5" />
          Richiedi Modifica
        </DialogTitle>
        <DialogDescription>
          Descrivi le modifiche richieste. L'amministrazione verrà notificata.
        </DialogDescription>
      </DialogHeader>
      <div className="mt-4">
        <Textarea
          value={modificationNotes}
          onChange={(e) => setModificationNotes(e.target.value)}
          placeholder="Descrivi le modifiche necessarie..."
          rows={4}
          data-testid="modification-notes-textarea"
        />
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Annulla
        </Button>
        <Button 
          onClick={onRequestModification}
          disabled={!modificationNotes.trim() || processingAction}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="confirm-modification-btn"
        >
          Invia Richiesta
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Dialog sconto (Solo Admin)
export const DiscountDialog = ({
  open,
  onOpenChange,
  discountPercentage,
  setDiscountPercentage,
  onApplyDiscount,
  processingAction
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Applica Sconto</DialogTitle>
        <DialogDescription>
          Inserisci la percentuale di sconto da applicare all'extra.
        </DialogDescription>
      </DialogHeader>
      <div className="mt-4">
        <Label htmlFor="discount">Percentuale Sconto (%)</Label>
        <Input
          id="discount"
          type="number"
          min="0"
          max="100"
          value={discountPercentage}
          onChange={(e) => setDiscountPercentage(parseFloat(e.target.value) || 0)}
          className="mt-1"
          data-testid="discount-percentage-input"
        />
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Annulla
        </Button>
        <Button 
          onClick={onApplyDiscount}
          disabled={processingAction || discountPercentage < 0 || discountPercentage > 100}
          className="btn-primary"
          data-testid="confirm-discount-btn"
        >
          Applica Sconto
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
